import { useEffect, useState } from "react";
import type { StaffPaneProps } from "./console-types";
import "./client-test-classification.css";

interface Classification { override: boolean | null; isTest: boolean }
type Choice = "auto" | "test" | "real";
function choice(value: boolean | null): Choice { return value === null ? "auto" : value ? "test" : "real"; }

/** Classification belongs to the client, independently of their team role.
 * The global filter consumes it; this card never changes access or data. */
export function ClientTestClassification(props: Pick<StaffPaneProps, "t" | "run" | "refresh"> & { clientId: string }) {
  const [classification, setClassification] = useState<Classification | null>(null);
  const [selected, setSelected] = useState<Choice>("auto");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [saved, setSaved] = useState(false);
  const [reload, setReload] = useState(0);
  const endpoint = `/api/admin/clients/${props.clientId}/test-classification`;
  const text = (key: string) => props.t(`staffConsole.testClassification.${key}`);

  useEffect(() => {
    const controller = new AbortController();
    setClassification(null);
    setError(false);
    void fetch(endpoint, { credentials: "include", signal: controller.signal }).then(async (response) => {
      if (!response.ok) throw new Error("classification_load_failed");
      const body = await response.json() as { ok?: boolean; classification?: Classification };
      const value = body.classification;
      if (!body.ok || !value || typeof value.isTest !== "boolean"
        || (value.override !== null && typeof value.override !== "boolean")) throw new Error("classification_invalid");
      if (controller.signal.aborted) return;
      setClassification(value);
      setSelected(choice(value.override));
    }).catch(() => { if (!controller.signal.aborted) setError(true); });
    return () => controller.abort();
  }, [endpoint, reload]);

  async function save() {
    setBusy(true);
    setError(false);
    setSaved(false);
    try {
      const response = await props.run(endpoint, { method: "PUT", body: { isTest: selected === "auto" ? null : selected === "test" } });
      if (!response.ok) throw new Error("classification_save_failed");
      const body = await response.json() as { ok?: boolean; classification?: Classification };
      if (!body.ok || !body.classification) throw new Error("classification_save_failed");
      setClassification(body.classification);
      setSaved(true);
      await props.refresh();
    } catch { setError(true); }
    finally { setBusy(false); }
  }

  return <article className="staff-card client-test-classification" data-admin-tool="test-classification">
    <h3>{text("title")}</h3>
    <p className="staff-card-hint">{text("hint")}</p>
    {!classification ? (
      error ? <div role="alert"><p>{text("loadError")}</p><button type="button" className="secondary-button compact-button" onClick={() => setReload((value) => value + 1)}>{text("reload")}</button></div>
        : <p role="status">{props.t("common.loading")}</p>
    ) : <>
      <p>{text(classification.isTest ? "currentTest" : "currentReal")}</p>
      <label htmlFor={`test-classification-${props.clientId}`}>{text("label")}</label>
      <select id={`test-classification-${props.clientId}`} value={selected} disabled={busy}
        onChange={(event) => { setSelected(event.target.value as Choice); setSaved(false); setError(false); }}>
        <option value="auto">{text("auto")}</option>
        <option value="test">{text("test")}</option>
        <option value="real">{text("real")}</option>
      </select>
      <p className="staff-card-hint">{text("filterHint")}</p>
      <div className="button-row">
        <button type="button" className="secondary-button compact-button" disabled={busy || selected === choice(classification.override)} onClick={() => void save()}>
          {text(busy ? "saving" : "save")}
        </button>
        {saved && <span className="message success-message" role="status">{text("saved")}</span>}
        {error && <span className="message error-message" role="alert">{text("saveError")}</span>}
      </div>
    </>}
  </article>;
}
